import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import { Button, ColoredButton } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--button-shadow);
  padding: 18px;
`;

const CardTitle = styled.h4`
  margin: 0 0 14px;
`;

const Note = styled.p`
  margin: 0 0 16px;
  opacity: 0.75;
  line-height: 1.45;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 18px;
`;

const SummaryItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--app-bg);
`;

const PreviewSourceGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 18px;
`;

const PreviewSourceCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  background: linear-gradient(180deg, var(--app-bg), var(--button-bg));
`;

const PreviewGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const PreviewCard = styled.a`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  color: inherit;
  text-decoration: none;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  background: var(--app-bg);
  box-shadow: var(--button-shadow);
  transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(0, 0, 0, 0.18);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.08);
  }
`;

const PreviewCover = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #d8eadb 0%, #f4d2c5 100%);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const PreviewFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  padding: 16px;
  background: linear-gradient(135deg, #d3ede2 0%, #f1a67d 100%);
  color: #1d1d1d;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
`;

const PreviewBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
`;

const PreviewBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PreviewStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  background: rgba(216, 159, 47, 0.16);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PreviewSourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  background: rgba(0, 0, 0, 0.05);
  font-size: 12px;
  font-weight: 600;
`;

const PreviewTitle = styled.h4`
  margin: 0;
  line-height: 1.3;
`;

const PreviewSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.45;
  opacity: 0.75;
`;

const PreviewDate = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
`;

const PreviewVenue = styled.div`
  font-size: 13px;
  opacity: 0.78;
`;

const PreviewSummary = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.82;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PreviewLink = styled.span`
  margin-top: auto;
  font-size: 13px;
  font-weight: 700;
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SourceItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 14px;
  background: var(--app-bg);
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const SourceTitle = styled.div`
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 13px;
  opacity: 0.75;
  line-height: 1.5;
`;

const SplitRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 1fr) auto;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  padding: 24px 0;
  opacity: 0.75;
`;

const SOURCE_CONFIG = {
  two_brewers: {
    label: "Two Brewers",
    note:
      "Match the Two Brewers venue to the correct Playground place, then import the latest party snapshot into the database.",
    importLabel: "Import Two Brewers snapshot now"
  },
  electrowerkz: {
    label: "Electrowerkz",
    note:
      "Match Electrowerkz to the correct Playground place, then import the latest club snapshot into the database.",
    importLabel: "Import Electrowerkz snapshot now"
  },
  fire_vauxhall: {
    label: "Fire Vauxhall",
    note:
      "Match Fire and Lightbox venue labels from the Fire Skiddle feed to the correct Playground places, then import the latest snapshot into the database.",
    importLabel: "Import Fire Vauxhall snapshot now"
  }
};

const SOURCE_LOADERS = {
  two_brewers: () => api.parties.two_brewers.listMappings(),
  electrowerkz: () => api.parties.electrowerkz.listMappings(),
  fire_vauxhall: () => api.parties.fire_vauxhall.listMappings()
};

const buildSourceState = buildValue =>
  Object.keys(SOURCE_CONFIG).reduce((acc, source) => {
    acc[source] = buildValue(source);
    return acc;
  }, {});

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return error.response.data.message || error.response.data.error || error.toString();
  }
  return error.toString();
};

const parseDateValue = value => {
  if (!value) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(part => Number(part));
    return new Date(year, month - 1, day, 12, 0, 0);
  }

  return new Date(value);
};

const formatDateTime = (value, fallback = "Not imported yet") => {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateValue(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const formatDate = (value, fallback = "Unknown") => {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateValue(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

const formatStatusLabel = status => {
  if (status === "current") {
    return "Tonight";
  }
  if (status === "upcoming") {
    return "Upcoming";
  }
  return status || "Snapshot";
};

const formatListingDate = listing => {
  if (!listing) {
    return "Dates unavailable";
  }

  if (listing.date_text) {
    return listing.date_text;
  }

  const dateValue = formatDate(listing.start_date || listing.end_date, "Dates unavailable");
  const timeParts = [listing.start_time, listing.end_time].filter(Boolean);
  return timeParts.length ? `${dateValue} ${timeParts.join(" - ")}` : dateValue;
};

class Parties extends React.Component {
  state = {
    reload: false,
    selections: buildSourceState(() => ({})),
    savingSlugs: buildSourceState(() => null),
    importingSnapshots: buildSourceState(() => false)
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  setSelection = (source, slug, placeId) => {
    this.setState(state => ({
      selections: {
        ...state.selections,
        [source]: {
          ...(state.selections[source] || {}),
          [slug]: placeId
        }
      }
    }));
  };

  saveMapping = async (source, slug, fallbackPlaceId = "") => {
    const sourceLabel = SOURCE_CONFIG[source].label;
    const selectedValue =
      (this.state.selections[source] && this.state.selections[source][slug]) ||
      fallbackPlaceId ||
      "";

    if (!selectedValue) {
      toasts.error(`Choose the Playground place that matches this ${sourceLabel} venue.`);
      return;
    }

    this.setState(state => ({
      savingSlugs: {
        ...state.savingSlugs,
        [source]: slug
      }
    }));

    try {
      const result = await api.parties[source].saveMapping({
        slug,
        place_id: Number(selectedValue),
        is_active: true
      });

      this.setState(state => ({
        selections: {
          ...state.selections,
          [source]: {
            ...(state.selections[source] || {}),
            [slug]: String(result.place_id)
          }
        }
      }));

      toasts.success(`Mapped ${result.venue_name} to ${result.place_name}.`);
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState(state => ({
        savingSlugs: {
          ...state.savingSlugs,
          [source]: null
        }
      }));
    }
  };

  importSnapshot = async source => {
    const sourceLabel = SOURCE_CONFIG[source].label;

    this.setState(state => ({
      importingSnapshots: {
        ...state.importingSnapshots,
        [source]: true
      }
    }));

    try {
      const result = await api.parties[source].importSnapshot({});
      toasts.success(
        `Imported ${result.totals.saved_listings} ${sourceLabel} listings across ${result.totals.venues} venues.`
      );
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState(state => ({
        importingSnapshots: {
          ...state.importingSnapshots,
          [source]: false
        }
      }));
    }
  };

  renderPreviewSection = payload => {
    const items = (payload && payload.items) || [];
    const sourceSummaries = (payload && payload.source_summaries) || [];
    const liveSourceCount = sourceSummaries.filter(summary => summary.snapshot_available).length;

    return (
      <Card>
        <CardTitle>Party Snapshot Preview</CardTitle>
        <Note>
          Quick visual check of the latest scraped club snapshots before import. This stays
          snapshot-driven, so you can sanity-check party titles, flyers, dates, and venue coverage
          before anything is written to the database.
        </Note>

        <SummaryGrid>
          <SummaryItem>
            <strong>Sources Live</strong>
            <Meta>
              {liveSourceCount} of {sourceSummaries.length || 0} club feeds
            </Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Snapshot Listings</strong>
            <Meta>{payload && payload.total_count ? payload.total_count : 0} parties</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Tonight</strong>
            <Meta>{payload && payload.current_count ? payload.current_count : 0} active today</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Upcoming</strong>
            <Meta>{payload && payload.upcoming_count ? payload.upcoming_count : 0} future parties</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Latest Snapshot</strong>
            <Meta>
              {formatDateTime(
                payload && payload.latest_snapshot_fetched_at,
                "No snapshot fetched yet"
              )}
            </Meta>
          </SummaryItem>
        </SummaryGrid>

        {sourceSummaries.length ? (
          <PreviewSourceGrid>
            {sourceSummaries.map(summary => (
              <PreviewSourceCard key={summary.source}>
                <SourceTitle>{summary.source_label}</SourceTitle>
                <Meta>
                  {summary.listing_count || 0} listings, {summary.current_count || 0} current,{" "}
                  {summary.upcoming_count || 0} upcoming
                </Meta>
                <Meta>
                  {summary.snapshot_fetched_at
                    ? `Fetched ${formatDateTime(summary.snapshot_fetched_at)}`
                    : "No snapshot fetched yet"}
                </Meta>
              </PreviewSourceCard>
            ))}
          </PreviewSourceGrid>
        ) : null}

        {items.length ? (
          <PreviewGrid>
            {items.map(item => {
              const cardProps = item.detail_url
                ? {
                    as: "a",
                    href: item.detail_url,
                    target: "_blank",
                    rel: "noreferrer"
                  }
                : { as: "div" };
              const fallbackLabel = item.source_label || item.venue_name || "Party";

              return (
                <PreviewCard
                  key={`${item.source}-${item.venue_slug}-${item.external_id || item.slug || item.title}`}
                  {...cardProps}
                >
                  <PreviewCover>
                    {item.cover_source_url ? (
                      <PreviewImage src={item.cover_source_url} alt={item.title || fallbackLabel} />
                    ) : (
                      <PreviewFallback>{fallbackLabel}</PreviewFallback>
                    )}
                  </PreviewCover>

                  <PreviewBody>
                    <PreviewBadgeRow>
                      <PreviewStatusBadge>{formatStatusLabel(item.status)}</PreviewStatusBadge>
                      <PreviewSourceBadge>{item.source_label}</PreviewSourceBadge>
                    </PreviewBadgeRow>

                    <div>
                      <PreviewTitle>{item.title || "Untitled party"}</PreviewTitle>
                      {item.subtitle ? <PreviewSubtitle>{item.subtitle}</PreviewSubtitle> : null}
                    </div>

                    <PreviewDate>{formatListingDate(item)}</PreviewDate>
                    <PreviewVenue>{item.venue_name}</PreviewVenue>
                    {item.summary ? <PreviewSummary>{item.summary}</PreviewSummary> : null}
                    {item.detail_url ? <PreviewLink>Open source page</PreviewLink> : null}
                  </PreviewBody>
                </PreviewCard>
              );
            })}
          </PreviewGrid>
        ) : (
          <EmptyState>No snapshot preview is available yet. Run a source sync first.</EmptyState>
        )}
      </Card>
    );
  };

  renderSourceSection = (source, payload) => {
    const config = SOURCE_CONFIG[source];
    const venues = (payload && payload.venues) || [];
    const candidates = (payload && payload.candidates) || [];
    const statusSummary = (payload && payload.status_summary) || {};
    const sourceSelections = this.state.selections[source] || {};
    const savingSlug = this.state.savingSlugs[source];
    const importingSnapshot = this.state.importingSnapshots[source];

    if (!venues.length) {
      return (
        <Card>
          <CardTitle>{config.label} Matching</CardTitle>
          <EmptyState>No {config.label} venues are configured yet.</EmptyState>
        </Card>
      );
    }

    return (
      <Card>
        <CardTitle>{config.label} Matching</CardTitle>
        <Note>{config.note}</Note>

        <SummaryGrid>
          <SummaryItem>
            <strong>Mapped Venues</strong>
            <Meta>
              {statusSummary.mapped_venue_count || 0} of {venues.length}
            </Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Imported Venues</strong>
            <Meta>{statusSummary.imported_venue_count || 0} with stored parties</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Upcoming Entries</strong>
            <Meta>{statusSummary.total_upcoming_party_count || 0} future parties</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Coverage Through</strong>
            <Meta>{formatDate(statusSummary.coverage_through_date)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Last Import</strong>
            <Meta>{formatDateTime(statusSummary.last_imported_at)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Snapshot Fetched</strong>
            <Meta>{formatDateTime(payload && payload.snapshot_fetched_at)}</Meta>
          </SummaryItem>
        </SummaryGrid>

        <ButtonRow>
          <ColoredButton
            type="button"
            onClick={() => this.importSnapshot(source)}
            disabled={importingSnapshot}
          >
            {importingSnapshot ? "Importing snapshot..." : config.importLabel}
          </ColoredButton>
        </ButtonRow>

        <SourceList>
          {venues.map(venue => {
            const selectedValue =
              sourceSelections[venue.slug] !== undefined
                ? sourceSelections[venue.slug]
                : venue.mapped_place_id
                  ? String(venue.mapped_place_id)
                  : "";
            const selectedCandidate = candidates.find(
              candidate => String(candidate.place_id) === String(selectedValue)
            );
            const addressValue =
              (selectedCandidate && selectedCandidate.address) ||
              venue.mapped_place_address ||
              "";
            const status = venue.status || null;

            return (
              <SourceItem key={`${source}-${venue.slug}`}>
                <SourceHeader>
                  <div>
                    <SourceTitle>{venue.venue_name}</SourceTitle>
                    <Meta>{venue.query}</Meta>
                    {venue.mapped_place_name ? (
                      <Meta>
                        Currently mapped to {venue.mapped_place_name}
                        {venue.mapped_place_id ? ` (#${venue.mapped_place_id})` : ""}
                      </Meta>
                    ) : (
                      <Meta>No place selected yet.</Meta>
                    )}
                    {status && status.has_imported_parties ? (
                      <React.Fragment>
                        <Meta>
                          {status.current_party_count || 0} current,{" "}
                          {status.upcoming_party_count || 0} upcoming,{" "}
                          {status.stored_party_count || 0} stored total
                        </Meta>
                        <Meta>
                          Next party {formatDate(status.next_start_date)}. Coverage through{" "}
                          {formatDate(status.coverage_through_date)}.
                        </Meta>
                        <Meta>Last imported {formatDateTime(status.last_imported_at)}</Meta>
                      </React.Fragment>
                    ) : venue.mapped_place_name ? (
                      <Meta>No {config.label} parties imported for this mapped place yet.</Meta>
                    ) : null}
                  </div>
                </SourceHeader>

                <SplitRow>
                  <div>
                    <Label>Playground place</Label>
                    <Select
                      value={selectedValue}
                      onChange={event => this.setSelection(source, venue.slug, event.target.value)}
                    >
                      <option value="">Choose a nightlife place</option>
                      {candidates.map(candidate => {
                        const mappedElsewhere =
                          candidate.mapping_slug && candidate.mapping_slug !== venue.slug;
                        const label = `${candidate.place_name} (#${candidate.place_id})${
                          mappedElsewhere
                            ? ` - already mapped to ${candidate.mapping_slug}`
                            : ""
                        }`;

                        return (
                          <option
                            key={`${source}-${candidate.place_id}`}
                            value={candidate.place_id}
                            disabled={mappedElsewhere}
                          >
                            {label}
                          </option>
                        );
                      })}
                    </Select>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Input
                      value={addressValue}
                      placeholder="Select a place to preview its address"
                      readOnly
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={() => this.saveMapping(source, venue.slug, selectedValue)}
                    disabled={!selectedValue || savingSlug === venue.slug}
                  >
                    {savingSlug === venue.slug ? "Saving..." : "Save match"}
                  </Button>
                </SplitRow>
              </SourceItem>
            );
          })}
        </SourceList>
      </Card>
    );
  };

  render() {
    const { reload } = this.state;

    return (
      <Page title="Parties">
        <Stack>
          <Async fetchProps={api.parties.listPreviews} reload={reload}>
            {payload => this.renderPreviewSection(payload)}
          </Async>
          {Object.keys(SOURCE_CONFIG).map(source => (
            <Async key={source} fetchProps={SOURCE_LOADERS[source]} reload={reload}>
              {payload => this.renderSourceSection(source, payload)}
            </Async>
          ))}
        </Stack>
      </Page>
    );
  }
}

export default Parties;
